<template>
  <LModal
    :visible="visible"
    :title="`${t('modules.keycloak.KeycloakUserDialog.title')} (${result?.KeycloakUsers.length ?? 0})`"
    size="xl"
    :submit="
      async () => {
        await goToKeycloakUsers();
        return { success: true };
      }
    "
    @cancel="_visible = false"
    @abort="_visible = false"
  >
    <Message class="mt-2 mb-2" severity="warn" icon="fas fa-exclamation-triangle">
      {{ t("modules.keycloak.KeycloakUserDialog.messages.nonRegisteredUsers") }}
    </Message>
    <SearchList
      v-if="result"
      v-model:value="result.KeycloakUsers"
      v-model:selected="selected"
      no-container-padding
      :attributes="attributes"
      :name="t('views.user.user')"
    >
      <template #action="{ data }">
        <div style="display: flex; gap: 0.5rem; justify-content: flex-end">
          <InfoTooltip>
            <template #activator>
              <LISAConfirmationButton
                severity="danger"
                size="small"
                :buttons="{
                  submit: {
                    severity: 'danger',
                    label: t('global.delete'),
                    icon: 'fas fa-trash-alt',
                  },
                }"
                @click="deleteKeycloakUser(data.item.id)"
              >
                <i class="fas fa-trash-alt" />
              </LISAConfirmationButton>
            </template>
            <div class="p-1">
              {{ t("modules.keycloak.KeycloakUserDialog.inputs.deleteButton.tooltip") }}
            </div>
          </InfoTooltip>

          <InfoTooltip>
            <template #activator>
              <LISAButton
                severity="primary"
                size="small"
                icon="fas fa-chevron-right"
                @click="gotoKeycloakUser(data.item.id)"
              />
            </template>
            <div class="p-1">
              {{ t("modules.keycloak.KeycloakUserDialog.inputs.goToUserButton.tooltip") }}
            </div>
          </InfoTooltip>
        </div>
      </template>
    </SearchList>
    <template #modal-ok>
      <i class="fas fa-users mr-2"></i>
      {{ t("modules.keycloak.KeycloakUserDialog.inputs.okButton.title") }}
    </template>
  </LModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import LModal from "../../../components/LModal.vue";
import { useGetKeycloakUsersQuery, useKeycloakUserDeleteMutation } from "../../../graphql";
import { hasRight } from "@/functions/authorizationState";
import Message from "primevue/message";
import { useTranslation } from "@/language/translation";
import { useRouter } from "vue-router";
import LISAButton from "../../../components/shared/primevue/LISAButton.vue";
import InfoTooltip from "../../../components/shared/InfoTooltip.vue";
import LISAConfirmationButton from "../../../components/shared/primevue/LISAConfirmationButton.vue";
import { useToast } from "primevue/usetoast";
import type { ToastMessageOptions } from "primevue/toast";
import { DateTime } from "luxon";
import type { ListAttributeType, SelectedKeys } from "@/components/SearchListHelper";
import SearchList from "@/components/SearchList.vue";
const { t } = useTranslation();
const router = useRouter();

const initialized = ref(false);
const hasUserWriteRight = computed<boolean>(() => {
  return hasRight("user.admin");
});
const _visible = ref(false);

const lastOpeningDate = ref<DateTime | null>(null);
const wasLastOpeingRecent = computed(() => {
  if (!lastOpeningDate.value) return false;

  if (lastOpeningDate.value.toISODate() !== DateTime.now().toISODate()) {
    return false;
  }
  return true;
});

onMounted(() => {
  const lastOpening = localStorage.getItem("KeyCloakUserDialogLastOpening");
  if (lastOpening) {
    lastOpeningDate.value = DateTime.fromISO(lastOpening);
  }
});

const visible = computed(
  () => _visible.value && hasUserWriteRight.value === true && !wasLastOpeingRecent.value,
);

watch(visible, (val) => {
  if (!val) {
    lastOpeningDate.value = DateTime.now();
    localStorage.setItem("KeyCloakUserDialogLastOpening", lastOpeningDate.value.toISO());
  }
});
const attributes = computed<ListAttributeType[]>(() => [
  {
    key: "username",
    label: t("views.user.options.username"),
  },
  {
    key: "firstName",
    label: t("components.user.UserHome.firstName"),
  },
  {
    key: "lastName",
    label: t("components.user.UserHome.lastName"),
  },
  {
    key: "email",
    label: t("components.user.UserHome.email"),
  },
  {
    key: "action",
    label: " ",
    custom: true,
    selectable: false,
    sortable: false,
    fit: false,
    cellStyle: "text-right",
  },
]);

function goToKeycloakUsers() {
  _visible.value = false;
  router.push("/administration/keycloak/users/notRegistered");
}

const { onResult, result, refetch } = useGetKeycloakUsersQuery(
  () => ({}),
  () => ({
    enabled: initialized.value === false && hasUserWriteRight.value === true,
    fetchPolicy: "network-only",
  }),
);

onResult((res) => {
  initialized.value = true;
  _visible.value = res?.data?.KeycloakUsers && res.data.KeycloakUsers.length > 0;
});

function gotoKeycloakUser(id: string | undefined) {
  _visible.value = false;
  router.push("/administration/keycloak/users/notRegistered").then(() => {
    router.push(`/administration/keycloak/users/create/${id}`);
  });
}

/**
 * Delete Keycloak User
 */

const toast = useToast();
const { mutate: deleteUser } = useKeycloakUserDeleteMutation();
async function deleteKeycloakUser(id?: string) {
  if (!id) return;
  try {
    await deleteUser({ id });
    const u = await refetch();
    if (u?.data?.KeycloakUsers && u.data.KeycloakUsers.length === 0) {
      _visible.value = false;
    }
  } catch (_error) {
    toast.add({
      severity: "error",
      summary: t("global.error"),
      detail: t("modules.keycloak.KeycloakUserDialog.errors.deleteUserFailed.detail"),
      life: 10000,
    } as ToastMessageOptions);
    return;
  }
}

const selected = ref<SelectedKeys<NonNullable<typeof result.value>["KeycloakUsers"]>>([
  "email",
  "firstName",
  "lastName",
  "username",
]);
</script>
